import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Paper,
  Divider,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useClasses from '../../helpers/useClasses';

/* Consistent non-serif fonts used across the whole page */
const SANS_FONT =
  '"Roboto", "Helvetica", "Arial", sans-serif';
const MONO_FONT =
  'ui-monospace, "SF Mono", "Segoe UI Mono", "Roboto Mono", Menlo, Monaco, Consolas, sans-serif';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    fontFamily: SANS_FONT,
  },
  content: {
    marginTop: theme.spacing(2),
  },
});

/* ------------------------------------------------------------------ */
/*  Presentational helpers                                            */
/* ------------------------------------------------------------------ */

const METHOD_COLORS = {
  GET: '#43a047',
  POST: '#17507f',
  DELETE: '#C22121',
  PUT: '#fb8c00',
};

const MethodChip = ({ method }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      minWidth: 58,
      textAlign: 'center',
      color: '#fff',
      fontWeight: 700,
      fontSize: '12px',
      letterSpacing: '0.5px',
      lineHeight: 1.6,
      backgroundColor: METHOD_COLORS[method] || '#555',
      padding: '2px 8px',
      borderRadius: '4px',
    }}
  >
    {method}
  </Box>
);

const ProBadge = () => (
  <Box
    component="span"
    sx={{
      color: '#fff',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.5px',
      backgroundColor: '#17507f',
      padding: '1px 5px',
      borderRadius: '5px',
    }}
  >
    PRO
  </Box>
);

const CodeBlock = ({ children }) => (
  <Box
    component="pre"
    sx={{
      backgroundColor: '#f5f7fa',
      border: '1px solid #e3e8ee',
      borderRadius: '6px',
      padding: '12px 16px',
      overflowX: 'auto',
      fontSize: '13px',
      fontFamily: MONO_FONT,
      lineHeight: 1.5,
      margin: 0,
      whiteSpace: 'pre',
    }}
  >
    {children}
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      color: 'text.secondary',
      fontWeight: 700,
      letterSpacing: '0.8px',
      mt: 2,
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

/* ------------------------------------------------------------------ */
/*  A single endpoint (collapsible)                                   */
/* ------------------------------------------------------------------ */

const Endpoint = ({ endpoint, apiURLRoot }) => {
  const fullUrl = `${apiURLRoot}${endpoint.path}`;

  return (
    <Accordion
      disableGutters
      sx={{
        mb: 1,
        border: '1px solid #e3e8ee',
        borderRadius: '6px !important',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <MethodChip method={endpoint.method} />
          <Typography sx={{ fontWeight: 600 }}>{endpoint.resource}</Typography>
          {endpoint.pro && <ProBadge />}
          <Typography
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '12px',
              color: 'text.secondary',
              ml: { xs: 0, sm: 'auto' },
            }}
          >
            {endpoint.path}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: '1px solid #eef1f5', pt: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {endpoint.description}
        </Typography>

        {endpoint.openFundingNote && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            If adding a note under the "Open Funding" category, please{' '}
            <Link href="#open-funding" underline="always">
              see additional requirements here
            </Link>
            .
          </Typography>
        )}

        <SectionLabel>Endpoint URL</SectionLabel>
        <CodeBlock>{fullUrl}</CodeBlock>

        {endpoint.needsAuth && (
          <>
            <SectionLabel>Authorization Header</SectionLabel>
            <CodeBlock>Authorization: {'{TOKEN}'}</CodeBlock>
          </>
        )}

        {endpoint.input && (
          <>
            <SectionLabel>Request Body</SectionLabel>
            <CodeBlock>{endpoint.input}</CodeBlock>
          </>
        )}

        <SectionLabel>cURL Example</SectionLabel>
        <CodeBlock>{endpoint.curl}</CodeBlock>

        {endpoint.output && (
          <>
            <SectionLabel>Response</SectionLabel>
            <CodeBlock>{endpoint.output}</CodeBlock>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

/* ------------------------------------------------------------------ */
/*  Endpoint data (built from the current apiURLRoot)                */
/* ------------------------------------------------------------------ */

const MERCHANT_OUTPUT = `[
  {
    "merchant": {
      "fein": "01-2345678",
      "legal_name": "ACME Company",
      "dba": "ACME",
      "address": "123 First Ave. S.",
      "street1": "",
      "street2": "",
      "city": "Los Angeles",
      "state": "CA",
      "business_phone": "123-456-7890",
      "business_startdate": "1/1/1985",
      "industry": "Combustables",
      "notes": [
        {
          "note": {
            "category": "Slow Pay",
            "note": "Merchant is a slow pay and/or default account.",
            "created_at": "2015-09-01 04:21:41 UTC",
            "added_by": "XYZ Funder"
          }
        }
      ]
    }
  }
]`;

const MERCHANT_OUTPUT_ADDITIONAL = `[
  {
    "merchant": {
      "fein": "01-2345678",
      "legal_name": "ACME Company",
      "dba": "ACME",
      "address": "123 First Ave. S.",
      "street1": "",
      "street2": "",
      "city": "Los Angeles",
      "state": "CA",
      "business_phone": "123-456-7890",
      "business_startdate": "1/1/1985",
      "industry": "Combustables",
      "notes": [
        {
          "note": {
            "category": "Slow Pay",
            "note": "Merchant is a slow pay and/or default account.",
            "submission_source": "broker",
            "broker_name": "Bob Broker",
            "created_at": "2015-09-01 04:21:41 UTC",
            "added_by": "XYZ Funder"
          }
        }
      ]
    }
  }
]`;

const POST_MERCHANT_OUTPUT = `{
  "fein": "01-2345678",
  "legal_name": "ACME Company",
  "dba": "ACME",
  "address": "123 First Ave. S.",
  "street1": "",
  "street2": "",
  "city": "Los Angeles",
  "state": "CA",
  "business_phone": "123-456-7890",
  "business_startdate": "1/1/1985",
  "industry": "Combustables",
  "notes": [
    {
      "note": {
        "category": "Slow Pay",
        "note": "Merchant is a slow pay and/or default account.",
        "created_at": "2015-09-01 04:21:41 UTC",
        "added_by": "XYZ Funder"
      }
    }
  ]
}`;

const POST_MERCHANT_OUTPUT_ADDITIONAL = `{
  "fein": "01-2345678",
  "legal_name": "ACME Company",
  "dba": "ACME",
  "address": "123 First Ave. S.",
  "street1": "",
  "street2": "",
  "city": "Los Angeles",
  "state": "CA",
  "business_phone": "123-456-7890",
  "business_startdate": "1/1/1985",
  "industry": "Combustables",
  "notes": [
    {
      "note": {
        "category": "Slow Pay",
        "note": "Merchant is a slow pay and/or default account.",
        "submission_source": "broker",
        "broker_name": "Bob Broker",
        "created_at": "2015-09-01 04:21:41 UTC",
        "added_by": "XYZ Funder"
      }
    }
  ]
}`;

const buildGroups = (apiURLRoot, subscriptionEnabled) => {
  const groups = [
    {
      id: 'authentication',
      title: 'Authentication',
      endpoints: [
        {
          method: 'POST',
          resource: 'Authentication',
          path: '/api/v2/token',
          description:
            'Authenticate with DataMerch. Returns a token used for all other API requests. Tokens expire every 24 hours.',
          needsAuth: false,
          input: `{
  "auth": {
    "authentication_token":"{AUTHENTICATION TOKEN}",
    "authentication_key":"{AUTHENTICATION KEY}"
  }
}`,
          curl: `curl -d '{"auth": {"authentication_token":"[AUTHENTICATION  TOKEN]","authentication_key":"[AUTHENTICATION KEY]"}}' ${apiURLRoot}/api/v2/get_token -H 'Content-Type: application/json'`,
          output: `[
  {
    "token": "{TOKEN TO USE FOR FUTURE API REQUESTS}"
  }
]`,
        },
      ],
    },
    {
      id: 'merchants',
      title: 'Merchants',
      endpoints: [
        {
          method: 'GET',
          resource: 'Merchant',
          path: '/api/v2/merchants/[9 digit EIN or Legal Name keywords]',
          description:
            'Search for a merchant by EIN or keywords in Legal Name. Returns a maximum of 5 merchants.',
          needsAuth: true,
          curl: `curl -H "Authorization: [TOKEN]" ${apiURLRoot}/api/v2/merchants/[9 digit EIN or Legal Name keywords]`,
          output: MERCHANT_OUTPUT,
        },
        {
          method: 'POST',
          resource: 'Merchant',
          path: '/api/v2/merchants',
          description: 'Add a new merchant.',
          openFundingNote: true,
          needsAuth: true,
          input: `{
  "fein": "01-2345678",
  "legal_name": "ACME Company",
  "dba": "ACME" //optional,
  "street1": "123 First St." //optional,
  "street2": "Suite 100" //optional,
  "city": "Los Angeles",
  "state": "CA",
  "business_phone": "123-456-7890" //optional,
  "business_startdate": "1/1/1985" //optional,
  "industry": "Combustables" //optional,
  "note_category": "Slow Pay",
  "note": "Merchant is a slow pay and/or default account."
}`,
          curl: `curl -d '{"fein": "01-2345678",
           "legal_name": "ACME Company",
           "dba": "ACME" //optional,
           "street1": "123 First St." //optional,
           "street2": "Suite 100" //optional,
           "city": "Los Angeles",
           "state": "CA",
           "business_phone": "123-456-7890" //optional,
           "business_startdate": "1/1/1985" //optional,
           "industry": "Combustables" //optional,
           "note_category": "Slow Pay",
           "note": "Merchant is a slow pay and/or default account."
          }' ${apiURLRoot}/api/v2/merchants -H 'Content-Type: application/json'  -H 'Authorization: [TOKEN]'`,
          output: POST_MERCHANT_OUTPUT,
        },
        {
          method: 'GET',
          resource: 'Merchant with Additional Data',
          path: '/api/v2/merchants_with_additional_data/[9 digit EIN or Legal Name keywords]',
          description:
            'Search for a merchant by EIN or keywords in Legal Name. Results include additional data in notes. Returns a maximum of 5 merchants.',
          pro: true,
          needsAuth: true,
          curl: `curl -H "Authorization: [TOKEN]" ${apiURLRoot}/api/v2/merchants_with_additional_data/[9 digit EIN or Legal Name keywords]`,
          output: MERCHANT_OUTPUT_ADDITIONAL,
        },
        {
          method: 'POST',
          resource: 'Merchant with Additional Data',
          path: '/api/v2/merchants_with_additional_data',
          description: 'Add a new merchant with additional note data.',
          openFundingNote: true,
          pro: true,
          needsAuth: true,
          input: `{
  "fein": "01-2345678",
  "legal_name": "ACME Company",
  "dba": "ACME" //optional,
  "street1": "123 First St." //optional,
  "street2": "Suite 100" //optional,
  "city": "Los Angeles",
  "state": "CA",
  "business_phone": "123-456-7890" //optional,
  "business_startdate": "1/1/1985" //optional,
  "industry": "Combustables" //optional,
  "note_category": "Slow Pay",
  "note": "Merchant is a slow pay and/or default account.",
  "submission_source": "broker" //optional (in-house or broker),
  "broker_name": "Bok Broker" //required if submission_source=broker
}`,
          curl: `curl -d '{"fein": "01-2345678",
           "legal_name": "ACME Company",
           "dba": "ACME" //optional,
           "street1": "123 First St." //optional,
           "street2": "Suite 100" //optional,
           "city": "Los Angeles",
           "state": "CA",
           "business_phone": "123-456-7890" //optional,
           "business_startdate": "1/1/1985" //optional,
           "industry": "Combustables" //optional,
           "note_category": "Slow Pay",
           "note": "Merchant is a slow pay and/or default account.",
           "submission_source": "broker" //optional (in-house or broker),
           "broker_name": "Bok Broker" //required if submission_source=broker
          }' ${apiURLRoot}/api/v2/merchants_with_additional_data -H 'Content-Type: application/json'  -H 'Authorization: [TOKEN]'`,
          output: POST_MERCHANT_OUTPUT_ADDITIONAL,
        },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      endpoints: [
        {
          method: 'POST',
          resource: 'Note',
          path: '/api/v2/notes',
          description: 'Add a note to an existing merchant.',
          openFundingNote: true,
          needsAuth: true,
          input: `{
  "fein": "01-2345678",
  "note_category": "Slow Pay",
  "note": "Merchant is a slow pay and/or default account."
}`,
          curl: `curl -d '{"fein": "01-2345678",
           "note_category": "Slow Pay",
           "note": "Merchant is a slow pay and/or default account."
          }' ${apiURLRoot}/api/v2/notes -H 'Content-Type: application/json'  -H 'Authorization: [TOKEN]'`,
          output: POST_MERCHANT_OUTPUT,
        },
        {
          method: 'POST',
          resource: 'Note with Additional Data',
          path: '/api/v2/notes_with_additional_data',
          description:
            'Add a note to an existing merchant with additional data.',
          openFundingNote: true,
          pro: true,
          needsAuth: true,
          input: `{
  "fein": "01-2345678",
  "note_category": "Slow Pay",
  "note": "Merchant is a slow pay and/or default account.",
  "submission_source": "broker",
  "broker_name": "Bob Broker"
}`,
          curl: `curl -d '{"fein": "01-2345678",
           "note_category": "Slow Pay",
           "note": "Merchant is a slow pay and/or default account.",
           "submission_source": "broker",
           "broker_name": "Bob Broker"
          }' ${apiURLRoot}/api/v2/notes_with_additional_data -H 'Content-Type: application/json'  -H 'Authorization: [TOKEN]'`,
          output: POST_MERCHANT_OUTPUT_ADDITIONAL,
        },
      ],
    },
  ];

  if (subscriptionEnabled) {
    groups.push({
      id: 'merchant-subscriptions',
      title: 'Merchant Subscriptions',
      endpoints: [
        {
          method: 'POST',
          resource: 'Merchant Subscription',
          path: '/api/v2/merchant_subscriptions',
          description: 'Add a merchant subscription.',
          needsAuth: true,
          input: `{
  "fein": "01-2345678"
}`,
          curl: `curl -d '{"fein": "01-2345678"
          }' ${apiURLRoot}/api/v2/merchant_subscriptions -H 'Content-Type: application/json'  -H 'Authorization: [TOKEN]'`,
          output: `{
  "fein": "01-2345678",
  "created_at": "2023-09-01 04:21:41 UTC"
}`,
        },
        {
          method: 'GET',
          resource: 'Merchant Subscription',
          path: '/api/v2/merchant_subscriptions/[EIN]',
          description: 'Find a merchant subscription.',
          needsAuth: true,
          curl: `curl -H "Authorization: [TOKEN]" ${apiURLRoot}/api/v2/merchant_subscriptions/[EIN]`,
          output: `[
  {
    "merchant_subscription": {
      "fein": "01-2345678",
      "created_at": "2023-09-01 04:21:41 UTC"
    }
  }
]`,
        },
        {
          method: 'DELETE',
          resource: 'Merchant Subscription',
          path: '/api/v2/delete_merchant_subscription/[EIN]',
          description: 'Delete a merchant subscription.',
          needsAuth: true,
          curl: `curl -H "Authorization: [TOKEN]" ${apiURLRoot}/api/v2/delete_merchant_subscription/[EIN]`,
          output: `{
  "errors": [error],
  "data": [result]
}`,
        },
      ],
    });
  }

  groups.push({
    id: 'search-history',
    title: 'Search History',
    endpoints: [
      {
        method: 'GET',
        resource: 'Search History',
        path: '/api/v2/search_history/[9 digit EIN]',
        description:
          'Get a list of the dates that an EIN was searched within the last 6 months.',
        pro: true,
        needsAuth: true,
        curl: `curl -H "Authorization: [TOKEN]" ${apiURLRoot}/api/v2/search_history/[9 digit EIN]`,
        output: `[
  {
    "created_at": "2024-04-17T00:00:06.000Z"
  }
]`,
      },
    ],
  });

  return groups;
};

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

const DevelopersNew = (props) => {
  const { account_merchant_subscription_enabled } = props;
  const classes = useClasses(styles);

  const isSandboxEnv = process.env.REACT_APP_ENVIRONMENT === 'SANDBOX';
  const [showSandboxURL, setShowSandboxURL] = useState(isSandboxEnv);
  const [apiURLRoot, setApiURLRoot] = useState(
    isSandboxEnv ? 'https://sandbox.api.datamerch.com' : 'https://api.datamerch.com'
  );

  const handleToggleURL = () => {
    setShowSandboxURL((prev) => {
      const next = !prev;
      setApiURLRoot(
        next ? 'https://sandbox.api.datamerch.com' : 'https://api.datamerch.com'
      );
      return next;
    });
  };

  const groups = buildGroups(apiURLRoot, account_merchant_subscription_enabled);

  const navItems = [
    { id: 'getting-started', label: 'Getting Started' },
    ...(isSandboxEnv ? [] : [{ id: 'sandbox', label: 'Sandbox' }]),
    { id: 'authentication-info', label: 'Authentication' },
    ...groups
      .filter((g) => g.id !== 'authentication')
      .map((g) => ({ id: g.id, label: g.title })),
    { id: 'note-categories', label: 'Note Categories' },
    { id: 'open-funding', label: 'Open Funding Notes' },
    ...(account_merchant_subscription_enabled
      ? [{ id: 'webhooks', label: 'Webhooks' }]
      : []),
  ];

  return (
    <div className={classes.root} style={{ fontFamily: SANS_FONT }}>
      <div className={classes.content}>
        <Card>
          <CardHeader
            title="DataMerch Developer's Guide"
            subheader="DataMerch API · Version 2 (Current)"
          />
          <Divider />
          <CardContent sx={{ fontFamily: SANS_FONT }}>
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
          {/* Sidebar navigation */}
          <Paper
            variant="outlined"
            sx={{
              width: { xs: '100%', md: 220 },
              flexShrink: 0,
              position: { md: 'sticky' },
              top: { md: 24 },
              p: 2,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', fontWeight: 700 }}
            >
              On this page
            </Typography>
            <Box
              component="nav"
              sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  href={`#${item.id}`}
                  underline="none"
                  sx={{
                    py: 0.5,
                    fontSize: '14px',
                    color: 'text.primary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Box>
          </Paper>

          {/* Main content */}
          <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
            {/* Getting started */}
            <Paper
              variant="outlined"
              sx={{ p: 3, mb: 3, borderRadius: 2 }}
              id="getting-started"
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Getting Started
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                How do I get my API credentials?
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                To make use of the DataMerch API, your account must be granted
                access to the API by a DataMerch administrator. To request
                access, please contact{' '}
                <a href="mailto:support@datamerch.com">support@datamerch.com</a>.
                If your account has been granted API access and you are an
                account administrator, you can log in to DataMerch and go to the
                "Account" page. The account page is accessed through the side
                menu displayed on the left when you are logged in. Once on the
                “Account” page, click “Account Members.” From there, you can
                grant or revoke API access as well as access a member's API
                credentials. Only members that have been granted access to the
                API will be given API credentials to use for Authentication.
                Once API access is granted, click the “Show” button to display
                each member’s unique API credentials.
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}
              >
                NOTE: If you do not see "Account" as a menu option, you are not
                an account administrator. Please contact your account
                administrator to request API access.
              </Typography>
            </Paper>

            {/* Sandbox */}
            {!isSandboxEnv && (
              <Paper
                variant="outlined"
                sx={{ p: 3, mb: 3, borderRadius: 2 }}
                id="sandbox"
              >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Sandbox
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  There is a sandbox environment available where you can try out
                  all of the available API endpoints in a non-production
                  environment. When requesting an authentication token from the
                  sandbox API, you need to provide your production Authentication
                  Token and Authentication Key, but prepend "test_" in front
                  (i.e. my production Authentication Token is "1234ABCD" so my
                  sandbox Authentication Token is "test_1234ABCD").
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}
                >
                  NOTE: Sandbox data is refreshed weekly. After a sandbox
                  refresh, the only merchant in the database will have an EIN of
                  "01-0000001".
                </Typography>
              </Paper>
            )}

            {/* Authentication */}
            <Paper
              variant="outlined"
              sx={{ p: 3, mb: 3, borderRadius: 2 }}
              id="authentication-info"
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Authentication
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                The DataMerch API uses token based authentication. Please see the
                "Authentication" endpoint below to retrieve a token. You will use
                the token for all other API requests by including the token as an
                "Authorization" header. <b>Tokens expire every 24 hours.</b> API
                credentials (as mentioned above) are only used for the
                Authentication endpoint.
              </Typography>
            </Paper>

            {/* Environment toggle */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Base URL
                </Typography>
                <Typography
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '13px',
                    color: 'primary.main',
                  }}
                >
                  {apiURLRoot}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: showSandboxURL ? 700 : 400 }}
                >
                  Sandbox
                </Typography>
                <Switch
                  checked={!showSandboxURL}
                  onChange={handleToggleURL}
                  color="primary"
                  name="showProductionURL"
                  inputProps={{ 'aria-label': 'toggle environment' }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: showSandboxURL ? 400 : 700 }}
                >
                  Production
                </Typography>
              </Box>
            </Paper>

            {/* Endpoint groups */}
            {groups.map((group) => (
              <Box key={group.id} id={group.id} sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, mb: 0.5 }}
                >
                  {group.title}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {group.endpoints.map((endpoint, idx) => (
                  <Endpoint
                    key={`${group.id}-${idx}`}
                    endpoint={endpoint}
                    apiURLRoot={apiURLRoot}
                  />
                ))}
              </Box>
            ))}

            {/* Note categories */}
            <Paper
              variant="outlined"
              sx={{ p: 3, mb: 3, borderRadius: 2 }}
              id="note-categories"
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Note Categories
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 0, '& li': { mb: 0.5 } }}>
                <li>Suspicious Activity</li>
                <li>Slow Pay</li>
                <li>Stacking History</li>
                <li>Default Account</li>
                <li>Default/Slow Pay Satisfied</li>
                <li>Open Funding (see below)</li>
                <li>Other</li>
              </Box>
            </Paper>

            {/* Open Funding notes */}
            <Paper
              variant="outlined"
              sx={{ p: 3, mb: 3, borderRadius: 2 }}
              id="open-funding"
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Open Funding Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                When creating a merchant or adding a note with a{' '}
                <b>note_category</b> of <b>"Open Funding"</b>, you must also
                include three additional fields: <b>date</b>, <b>amount</b>, and{' '}
                <b>status</b>. These apply to all merchant and note create
                endpoints (including the "with additional data" variants).
              </Typography>
              <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0, '& li': { mb: 0.5 } }}>
                <li>
                  <b>date</b> — funding date in MM/DD/YYYY format (e.g.
                  08/29/2026). Required.
                </li>
                <li>
                  <b>amount</b> — a plain number, no commas or symbols (e.g.
                  500 or 500.00). Required.
                </li>
                <li>
                  <b>status</b> — must be one of: <b>Active</b>,{' '}
                  <b>Reduced Payments</b>, <b>Paid in Full</b>, or{' '}
                  <b>Renewed</b>. Required.
                </li>
              </Box>
              <Typography
                variant="body2"
                sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}
              >
                If any of date, amount, or status is missing or malformed, the
                request is rejected with a 422 error. These fields are combined
                with the note text and stored in the note itself (shown below),
                so they are returned as part of the note in responses.
              </Typography>
              <SectionLabel>Request Body (additional fields)</SectionLabel>
              <CodeBlock>
                {`{
  "fein": "01-2345678",
  "note_category": "Open Funding",
  "note": "testing",
  "date": "08/29/2026",
  "amount": "500",
  "status": "Active"
}`}
              </CodeBlock>
              <SectionLabel>Resulting Stored Note</SectionLabel>
              <CodeBlock>
                {`"note": "Date: 08/29/2026 | Amount: 500 | Status: Active | Note: testing"`}
              </CodeBlock>
            </Paper>

            {/* Webhooks */}
            {account_merchant_subscription_enabled && (
              <Paper
                variant="outlined"
                sx={{ p: 3, mb: 3, borderRadius: 2 }}
                id="webhooks"
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Merchant Subscription Webhooks
                  </Typography>
                  <ProBadge />
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  If you have a webhook URL configured for merchant
                  subscriptions, in your account settings, the following payload
                  will be delivered when a change is made to a merchant EIN that
                  you are subscribed to.
                </Typography>
                <SectionLabel>Payload</SectionLabel>
                <CodeBlock>
                  {`{
  "Merchant EIN": "[EIN]",
  "Merchant Name": "[Merchant Name]",
  "Action": "Note Added",
  "Note Category": "Other",
  "Note": "This is an example of the webhook payload",
  "Merchant URL": "[URL for merchant in DataMerch]",
  "Action Performed By": "[Name of Funder who performed action]"
}`}
                </CodeBlock>
              </Paper>
            )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    account_merchant_subscription_enabled:
      state.auth.user.account_merchant_subscription_enabled,
  };
};

export default connect(mapStateToProps)(DevelopersNew);
